export function deriveRendererModel(state) {
  return Object.freeze({
    eventCount: state.events.length,
    traceCount: state.traces.length,
    requestCount: state.requests.length,
    decision: state.decisions.at(-1)?.decision || null,
    canonicalCount: state.canonicalRecords.length,
    allowedTransitions: Object.freeze(state.rendererTransitions.map((item) => item.type)),
    reasonCodes: Object.freeze(state.timeline.flatMap((item) => item.reasonCodes || []))
  });
}

export function getInspectableEntity(state, type, index) {
  const entities = {
    event: state.events,
    trace: state.traces,
    request: state.requests,
    decision: state.decisions,
    canonical: state.canonicalRecords
  }[type];
  return entities?.[index] || null;
}

export function describeRecallPath(request, predicatesEvaluated) {
  if (!predicatesEvaluated) return 'not evaluated';
  if (!request?.predicateResults?.recallPathValid) return 'direct attempt blocked';
  return request.recallDerivedInputPresent ? 're-ingested · lineage preserved' : 'no recall-derived input';
}

export function describeAuditReasonHistory(audit, reasonCodes) {
  return Object.freeze({
    reasonCodes: Object.freeze([...reasonCodes]),
    historical: audit?.decision === 'ALLOW' && reasonCodes.length > 0
  });
}

export function getScenarioInsight(scenarioId) {
  return scenarioId === 'salient-provisional'
    ? 'High salience and confidence can raise attention, but neither authorizes canonical persistence.'
    : null;
}

export function canAnimateCanonicalTransition(state) {
  const last = state.rendererTransitions.findLast((item) => item.type === 'CANONICAL_COMMITTED');
  if (!last) return false;
  const recordIndex = state.transitionRecords.findIndex((item) => item.transitionRecordId === last.transitionRecordId);
  if (recordIndex < 0) return false;
  const record = state.transitionRecords[recordIndex];
  const expectedPreviousHash = recordIndex === 0 ? 'GENESIS' : state.transitionRecords[recordIndex - 1].recordHash;
  const canonical = state.canonicalRecords.find((item) => item.transitionRecordId === record.transitionRecordId);
  return Boolean(canonical && validateTransition(record, { expectedPreviousHash }) && validateCanonicalRecord(canonical, record, { expectedPreviousHash }));
}

export const explainFromReasonCode = (code) => ({
  INSUFFICIENT_INDEPENDENT_DIMENSIONS: 'At least two independent evidence dimensions are required; repetition inside one dimension does not qualify.',
  RECALL_CANNOT_DIRECTLY_PROMOTE: 'Recall can raise attention, but recalled content must be re-ingested as a new Event before evaluation.',
  REFLEX_CANNOT_DIRECTLY_WRITE: 'A reflex-originated signal cannot write memory directly; it must enter through ordinary Event admission.',
  REGIME_NOT_STABLE: 'Canonical commitment is suspended while the operating regime is unstable or stabilizing.',
  STABILIZATION_INCOMPLETE: 'The five-step demo stabilization window is not complete.',
  PROVENANCE_INCOMPLETE: 'Required provenance is incomplete, so the promotion packet is quarantined.',
  CONTRADICTORY_EVIDENCE: 'Contradictory evidence cannot support authorization.'
}[code] || 'The engine emitted this outcome as part of its deterministic transition sequence.');
import { validateTransition } from '../contract/transition-log.js';
import { validateCanonicalRecord } from '../models/canonical-record.js';
